import { useState, useEffect, useCallback, useRef } from 'react';
import type { IdeaTask, TaskStatus, TaskType, Priority } from '@/lib/types';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { BlockedReasonModal, DoneNoteModal } from './TaskModals';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Calendar as CalendarIcon,
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  FlaskConical,
  Hammer,
  Search,
  Users,
  FileText,
  Sparkles,
  Trash2,
  Loader2,
} from 'lucide-react';

interface TaskDetailSheetProps {
  task: IdeaTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateTask: (taskId: string, updates: Partial<IdeaTask>) => Promise<boolean>;
  onDeleteTask: (taskId: string) => Promise<boolean>;
  doingCount: number;
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: typeof Circle; className: string }> = {
  next: { label: 'Next', icon: Circle, className: 'text-muted-foreground' },
  doing: { label: 'Doing', icon: Clock, className: 'text-primary' },
  blocked: { label: 'Blocked', icon: AlertCircle, className: 'text-destructive' },
  done: { label: 'Done', icon: CheckCircle2, className: 'text-primary' },
};

const TYPE_CONFIG: Record<TaskType, { label: string; icon: typeof FlaskConical }> = {
  experiment: { label: 'Experiment', icon: FlaskConical },
  build: { label: 'Build', icon: Hammer },
  research: { label: 'Research', icon: Search },
  outreach: { label: 'Outreach', icon: Users },
  admin: { label: 'Admin', icon: FileText },
};

const PRIORITY_STYLES: Record<Priority, string> = {
  low: 'bg-muted/50 text-muted-foreground',
  medium: 'bg-secondary/30 text-secondary-foreground',
  high: 'bg-primary/20 text-primary',
};

const WIP_LIMIT = 3;

export function TaskDetailSheet({
  task,
  open,
  onOpenChange,
  onUpdateTask,
  onDeleteTask,
  doingCount,
}: TaskDetailSheetProps) {
  // Local form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState<TaskType>('experiment');
  const [priority, setPriority] = useState<Priority>('medium');
  const [estimatedHours, setEstimatedHours] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [status, setStatus] = useState<TaskStatus>('next');

  // Modal states
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<TaskStatus | null>(null);

  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize form when task changes
  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      setDescription(task.description || '');
      setTaskType(task.task_type);
      setPriority(task.priority);
      setEstimatedHours(task.estimated_hours?.toString() || '');
      setStartDate(task.start_date ? new Date(task.start_date) : undefined);
      setDueDate(task.due_date ? new Date(task.due_date) : undefined);
      setStatus(task.status);
    }
  }, [task]);

  // Debounced save function
  const debouncedSave = useCallback(
    (updates: Partial<IdeaTask>) => {
      if (!task) return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        setSaving(true);
        await onUpdateTask(task.id, updates);
        setSaving(false);
      }, 500);
    },
    [task, onUpdateTask]
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    debouncedSave({ title: value });
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    debouncedSave({ description: value || null });
  };

  const handleTypeChange = (value: TaskType) => {
    setTaskType(value);
    debouncedSave({ task_type: value });
  };

  const handlePriorityChange = (value: Priority) => {
    setPriority(value);
    debouncedSave({ priority: value });
  };

  const handleHoursChange = (value: string) => {
    setEstimatedHours(value);
    const hours = parseFloat(value);
    debouncedSave({ estimated_hours: isNaN(hours) ? null : hours });
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
    debouncedSave({ start_date: date ? format(date, 'yyyy-MM-dd') : null });
  };

  const handleDueDateChange = (date: Date | undefined) => {
    setDueDate(date);
    debouncedSave({ due_date: date ? format(date, 'yyyy-MM-dd') : null });
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (!task || newStatus === status) return;

    // WIP limit check
    if (newStatus === 'doing' && status !== 'doing' && doingCount >= WIP_LIMIT) {
      toast.error('Finish something. Then start something.', {
        description: `WIP limit: max ${WIP_LIMIT} tasks in Doing`,
      });
      return;
    }

    // Blocked requires reason
    if (newStatus === 'blocked') {
      setPendingStatus(newStatus);
      setShowBlockedModal(true);
      return;
    }

    // Done requires note
    if (newStatus === 'done') {
      setPendingStatus(newStatus);
      setShowDoneModal(true);
      return;
    }

    setStatus(newStatus);
    await onUpdateTask(task.id, { status: newStatus });
  };

  const handleBlockedConfirm = async (reason: string) => {
    if (!task) return;
    setStatus('blocked');
    await onUpdateTask(task.id, { status: 'blocked', blocked_reason: reason });
    setPendingStatus(null);
  };

  const handleDoneConfirm = async (note: string, evidence: string) => {
    if (!task) return;
    setStatus('done');
    await onUpdateTask(task.id, {
      status: 'done',
      done_note: note || null,
      evidence: evidence || task.evidence,
    });
    setPendingStatus(null);
  };

  const handleDoneSkip = async () => {
    if (!task) return;
    setStatus('done');
    await onUpdateTask(task.id, { status: 'done' });
    setPendingStatus(null);
  };

  const handleDelete = async () => {
    if (!task) return;
    if (confirm('Delete this task?')) {
      await onDeleteTask(task.id);
      onOpenChange(false);
    }
  };

  if (!task) return null;

  const StatusIcon = STATUS_CONFIG[status].icon;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-lg">Edit Task</SheetTitle>
              <div className="flex items-center gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {task.source === 'ai' && (
                  <Badge variant="secondary" className="gap-1 text-[10px]">
                    <Sparkles className="h-3 w-3" />
                    AI Generated
                  </Badge>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-5">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="task-title">Title</Label>
              <Input
                id="task-title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Task title"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                placeholder="What needs to be done?"
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => handleStatusChange(v as TaskStatus)}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <StatusIcon className={cn('h-4 w-4', STATUS_CONFIG[status].className)} />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className={cn('h-4 w-4', config.className)} />
                          {config.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Blocked reason display */}
            {status === 'blocked' && task.blocked_reason && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-xs text-destructive">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  {task.blocked_reason}
                </p>
              </div>
            )}

            {/* Done note display */}
            {status === 'done' && task.done_note && (
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-md">
                <p className="text-xs text-primary">
                  <CheckCircle2 className="h-3 w-3 inline mr-1" />
                  {task.done_note}
                </p>
              </div>
            )}

            {/* Type & Priority row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={taskType} onValueChange={(v) => handleTypeChange(v as TaskType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {config.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={(v) => handlePriorityChange(v as Priority)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['high', 'medium', 'low'] as Priority[]).map((p) => (
                      <SelectItem key={p} value={p}>
                        <Badge className={cn('capitalize', PRIORITY_STYLES[p])}>{p}</Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Estimated hours */}
            <div className="space-y-2">
              <Label htmlFor="task-hours">Estimated Hours</Label>
              <Input
                id="task-hours"
                type="number"
                step="0.5"
                min="0"
                value={estimatedHours}
                onChange={(e) => handleHoursChange(e.target.value)}
                placeholder="e.g. 2"
              />
            </div>

            {/* Dates row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'MMM d') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={handleStartDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !dueDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, 'MMM d') : 'Pick date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={handleDueDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Delete */}
            <div className="pt-4 border-t">
              <Button
                variant="ghost"
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Task
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modals for status transitions */}
      <BlockedReasonModal
        open={showBlockedModal}
        onOpenChange={(open) => {
          setShowBlockedModal(open);
          if (!open) setPendingStatus(null);
        }}
        onConfirm={handleBlockedConfirm}
        taskTitle={task.title}
      />

      <DoneNoteModal
        open={showDoneModal}
        onOpenChange={(open) => {
          setShowDoneModal(open);
          if (!open) setPendingStatus(null);
        }}
        onConfirm={handleDoneConfirm}
        onSkip={handleDoneSkip}
        taskTitle={task.title}
      />
    </>
  );
}
